import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LANDING } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { Radio, Users, MessageCircle, Sparkles } from "lucide-react";
import PublicSidePill, { readPublicSide } from "@/components/PublicSidePill";
import SiteFooter from "@/components/SiteFooter";

const TESTFLIGHT_URL = "https://testflight.apple.com/join/392dP3J1";
const inNativeShell = () => { try { return !!window.Capacitor?.isNativePlatform?.(); } catch { return false; } };

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ label, value, testid }) => (
  <div className="card-surface p-6 hover-lift" data-testid={testid}>
    <div className="font-display text-5xl text-white">{value}</div>
    <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">{label}</div>
  </div>
);

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ live: 0, posts: 0, communities: 0, members: 0 });
  const [side, setSide] = useState(readPublicSide);
  const [previewStreams, setPreviewStreams] = useState([]);
  const [previewCommunities, setPreviewCommunities] = useState([]);

  useEffect(() => {
    if (user) navigate("/feed", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Refetch marketing previews whenever the visitor flips sides.
    axios
      .get(`${API_URL}/api/public/live-now`, { params: { side } })
      .then(({ data }) => setPreviewStreams((data.streams || []).slice(0, 4)))
      .catch(() => setPreviewStreams([]));
    axios
      .get(`${API_URL}/api/communities`, { params: { side } })
      .then(({ data }) => setPreviewCommunities((data || []).filter(c => c.visibility !== "private").slice(0, 4)))
      .catch(() => setPreviewCommunities([]));
  }, [side]);

  return (
    <div data-testid={LANDING.root} className="grain min-h-screen relative">
      {/* Ambient background image */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1767713421795-ca09a9d05c38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxleGNsdXNpdmUlMjBzcGVha2Vhc3klMjBkYXJrfGVufDB8fHx8MTc4NDc1NTc3Mnww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(to bottom, black 0%, black 40%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 40%, transparent 100%)",
        }}
      />
      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-14">
        {/* header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-lg">🐼</div>
            <span className="font-display text-lg sm:text-2xl tracking-tight whitespace-nowrap">My Panda X</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <PublicSidePill value={side} onChange={setSide} className="hidden sm:inline-flex" />
            <Link to="/login">
              <Button
                variant="ghost"
                className="rounded-none border border-white/10 text-white hover:bg-white/5 px-3 sm:px-4"
              >
                Log in
              </Button>
            </Link>
            <Link to="/register">
              <Button
                data-testid={LANDING.ctaSecondary}
                className="btn-crimson rounded-none px-3 sm:px-5"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>

        {/* hero */}
        <section data-testid={LANDING.hero} className="mt-24 lg:mt-40 max-w-4xl">
          <div className="text-xs uppercase tracking-[0.4em] text-rose-500 mb-6">
            18+ · Members only
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight">
            Enter the <span className="text-rose-500">Vault</span>.
          </h1>
          <p className="mt-8 text-lg text-zinc-300 max-w-xl leading-relaxed">
            A premium community for creators — live streams, social feeds, and real connections.
            My Panda X now on the web.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-3">
            <Link to="/register" className="w-full sm:w-auto">
              <Button
                data-testid={LANDING.ctaPrimary}
                className="btn-crimson rounded-none px-8 py-7 text-base w-full sm:w-auto"
              >
                Enter the Vault →
              </Button>
            </Link>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link
                to="/live-now"
                className="inline-flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300 transition-colors py-2 whitespace-nowrap"
                data-testid="landing-live-now"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" />
                See who's live now →
              </Link>
              <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors py-2 whitespace-nowrap">
                Already a member? Log in
              </Link>
            </div>
          </div>

          {!inNativeShell() && (
            <a
              href={TESTFLIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-testflight-link"
              className="mt-8 inline-flex items-center gap-3 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] px-5 py-3 transition-colors group"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white shrink-0" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.3em] text-rose-400">Now on iPhone</span>
                <span className="block text-sm text-white group-hover:text-rose-200 transition-colors">
                  Join the myPandaX beta on TestFlight →
                </span>
              </span>
            </a>
          )}
        </section>

        {/* stats */}
        <section className="mt-24 lg:mt-40 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <StatCard label="Live now" value={stats.live} testid={LANDING.statLive} />
          <StatCard label="Posts" value={stats.posts} testid={LANDING.statPosts} />
          <StatCard label="Communities" value={stats.communities} testid={LANDING.statCommunities} />
          <StatCard label="Members" value={stats.members} testid={LANDING.statMembers} />
        </section>

        {/* side-aware marketing previews */}
        <section className="mt-16 lg:mt-24" data-testid="landing-side-previews">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-rose-500 mb-2">Preview by side</div>
              <h2 className="font-display text-3xl sm:text-4xl">
                What's happening on the{" "}
                <span className="text-rose-400">{side === "lgbtq" ? "LGBTQ" : "Straight"}</span> side
                <span className="text-zinc-500"> right now.</span>
              </h2>
            </div>
            <PublicSidePill value={side} onChange={setSide} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div data-testid="landing-preview-live">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-rose-500 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" /> Live Now
                </div>
                <Link to="/live-now" className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-rose-400">
                  See all →
                </Link>
              </div>
              {previewStreams.length === 0 ? (
                <div className="card-surface p-6 text-sm text-zinc-500">
                  No one is live on the {side === "lgbtq" ? "LGBTQ" : "Straight"} side right now. Be the first when you sign up.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {previewStreams.map((s) => (
                    <Link
                      key={s.id}
                      to={`/register?ref=live_${s.id}`}
                      data-testid={`landing-live-card-${s.id}`}
                      className="card-surface overflow-hidden hover-lift group"
                    >
                      <div className="aspect-[16/9] bg-zinc-900 relative overflow-hidden">
                        {s.thumbnail_url ? (
                          <img src={s.thumbnail_url} alt={s.title || "Untitled Live"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-600">
                            <Radio size={22} />
                          </div>
                        )}
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-rose-600 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5">
                          <span className="w-1 h-1 rounded-full bg-white pulse-dot" /> Live
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="text-xs font-medium truncate">{s.title || "Untitled Live"}</div>
                        <div className="text-[10px] text-zinc-500 truncate mt-0.5">@{s.host_handle}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="landing-preview-communities">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-rose-500 flex items-center gap-2">
                  <Users size={11} /> Trending Communities
                </div>
                <Link to="/register" className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-rose-400">
                  Join to explore →
                </Link>
              </div>
              {previewCommunities.length === 0 ? (
                <div className="card-surface p-6 text-sm text-zinc-500">
                  No communities on this side yet. Be the first to start one.
                </div>
              ) : (
                <ul className="space-y-2">
                  {previewCommunities.map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`/register?ref=community_${c.id}`}
                        data-testid={`landing-community-${c.id}`}
                        className="card-surface p-4 flex items-center justify-between gap-4 hover-lift group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar className="w-10 h-10 border border-white/10 shrink-0">
                            <AvatarImage src={c.cover_url} />
                            <AvatarFallback className="bg-rose-600 text-white text-xs">
                              {c.name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            <div className="text-[11px] text-zinc-500 truncate">
                              {(c.members_count ?? 0).toLocaleString()} members
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-rose-500 group-hover:text-rose-400">
                          Explore →
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* features */}
        <section className="mt-24 lg:mt-32 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Radio, title: "Go Live", body: "Broadcast to your fans. Chat, tip, connect in real time." },
            { icon: MessageCircle, title: "Social Feed", body: "Post text, media and updates for the whole vault." },
            { icon: Users, title: "Communities", body: "Join niche rooms — from Music & Mixes to Panda Family." },
            { icon: Sparkles, title: "Gifts & Coins", body: "Tip your favorite creators with coins. Real support." },
          ].map((f) => (
            <div key={f.title} className="card-surface p-6 hover-lift">
              <f.icon size={22} className="text-rose-500 mb-4" strokeWidth={1.5} />
              <div className="font-display text-xl mb-2">{f.title}</div>
              <div className="text-sm text-zinc-400 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </section>

        <footer className="mt-24 lg:mt-40 pb-4 border-t border-white/5 pt-8 flex items-center justify-between text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} My Panda X · mypandax.com</div>
          <div className="uppercase tracking-[0.3em]">Adults 18+ only</div>
        </footer>
      </div>
      <SiteFooter />
    </div>
  );
}
